import { ReactElement, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  IconButton,
  InputBase,
  InputAdornment,
  CircularProgress,
  Button,
  Avatar,
  Chip,
  SxProps,
  Theme,
} from "@mui/material";
import { Add, Check, KeyboardArrowDown } from "@mui/icons-material";
import { CiSearch } from "react-icons/ci";

import DialogMenu from "../dialog-menu";
import {
  setSelectedClient,
  setSelectedTenant,
  useGetAllTenantsQuery,
} from "@/services";
import { useAppDispatch, useAppSelector } from "@/store";
import { useDebounce } from "@/hooks";
import { company_placeholder } from "@/constants";
import { useLocation, useNavigate } from "react-router-dom";

interface TenantPickerProps {
  sx?: SxProps<Theme>;
}

const TenantPicker: React.FC<TenantPickerProps> = ({ sx }): ReactElement => {
  const dispatch = useAppDispatch();
  const { tenantManagement: tenants, selectedTenant } = useAppSelector(
    (state) => state.tenantManagement
  );
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [search, setSearch] = useState("");

  const open = useMemo(() => Boolean(anchorEl), [anchorEl]);

  function openClientPickerMenu(event: React.MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  const closeClientPickerMenu = (): void => {
    setAnchorEl(null);
    setSearch("");
  };

  const filterClients = useDebounce(setSearch, 1200);

  const { isLoading, isFetching } = useGetAllTenantsQuery(
    { searchKeyword: search, pageNo: 1, pageSize: 25 },
    {
      refetchOnMountOrArgChange: true,
      skip: !open,
    }
  );

  return (
    <>
      <Button
        id="client-picker-anchor"
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        aria-controls={open ? "client-picker-menu" : undefined}
        sx={{
          width: { sm: 267 },
          justifyContent: "flex-start",
          padding: {
            xs: 0.5,
            sm: "10px 12px",
          },
          borderRadius: "4px",
          textAlign: "left",
          whiteSpace: "nowrap",
          minWidth: "initial",
          border: { xs: "1px solid tranparent", sm: 0 },
          "&[aria-expanded='true']": {
            zIndex: 1500,
            border: {
              xs: "1px solid #0B494B",
              sm: 0,
            },
            boxShadow: {
              xs: "0px 0px 0px 4px #0B494B3D",
              sm: "0px 1px 2px 0px rgba(78, 78, 78, 0.05), 0px 0px 0px 4px rgba(11, 73, 75, 0.24)",
            },
          },
          ...sx,
        }}
        onClick={openClientPickerMenu}
      >
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            width: "100%",
            gap: "10px",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          {isLoading ? (
            <CircularProgress size={18} />
          ) : (
            <>
              <Box
                sx={{
                  width: {
                    xs: 30,
                    sm: 34,
                  },
                  height: {
                    xs: 30,
                    sm: 34,
                  },
                  borderRadius: "50%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "#CDD3D3",
                }}
              >
                {selectedTenant ? (
                  <Avatar
                    src={selectedTenant.companyLogo || undefined}
                    sx={{
                      width: "100%",
                      height: "100%",
                    }}
                  />
                ) : (
                  <Add
                    sx={{
                      color: "#0b490b",
                      width: {
                        xs: 18,
                        sm: 24,
                      },
                    }}
                  />
                )}
              </Box>

              <Box
                sx={{
                  flex: 1,
                  overflow: "hidden",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={0.5}
                  sx={{
                    color: selectedTenant ? "#8E9494" : "#030712",
                    height: selectedTenant ? "14px" : "auto",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: selectedTenant ? 12 : 14,
                      fontWeight: selectedTenant ? 400 : 500,
                    }}
                  >
                    Select tenant
                  </Typography>
                  <KeyboardArrowDown sx={{ color: "#8E9494", fontSize: 14 }} />
                </Stack>

                {selectedTenant ? (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 14,
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "pre",
                    }}
                  >
                    {selectedTenant.companyName}{" "}
                    <Chip
                      sx={{
                        backgroundColor:
                          selectedTenant.tenantType === "SERVICE_PROVIDER"
                            ? "rgba(234, 67, 52, 1)"
                            : "rgba(52, 168, 83, 1)",
                        color: "#fff",
                        fontSize: 8,
                        fontWeight: 500,
                        height: 18,
                        borderRadius: 1,
                        ml: 0.4,
                        mb: 0.2,
                      }}
                      label={
                        selectedTenant.tenantType === "SERVICE_PROVIDER"
                          ? "SP"
                          : "Client"
                      }
                      size="small"
                    />
                  </Typography>
                ) : null}
              </Box>
            </>
          )}
        </Stack>
      </Button>

      <DialogMenu
        sx={{
          "& .MuiPaper-root": {
            width: "320px",
            borderRadius: "4px",
            border: 0,
          },
        }}
        id="client-picker-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={closeClientPickerMenu}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box sx={{ p: "10px" }}>
          <InputBase
            fullWidth
            sx={{
              backgroundColor: "#F1F2F3",
              border: "1px solid #EFEFEF",
              padding: "12px 1rem",
              borderRadius: "8px",
              mb: "5px",
              fontSize: "14px",
              fontFamily: "Plus Jakarta Sans",
              fontWeight: 500,
              lineHeight: "20px",
              "& .MuiInputBase-input": {
                height: "initial",
              },
            }}
            placeholder="Search"
            startAdornment={
              <InputAdornment position="start">
                <CiSearch size={18} />
              </InputAdornment>
            }
            endAdornment={
              isFetching ? (
                <InputAdornment position="end">
                  <CircularProgress size={18} />
                </InputAdornment>
              ) : null
            }
            onChange={(e) => filterClients(e.target.value)}
          />

          {!isFetching && tenants.length === 0 ? (
            <Box py="5px" px="10px">
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "Plus Jakarta Sans",
                }}
              >
                No Tenants Found
              </Typography>
            </Box>
          ) : (
            <Box
              className="client-list"
              sx={{
                maxHeight: 300,
                overflow: "auto",
                "&::-webkit-scrollbar": {
                  width: 3,
                },
                "&::-webkit-scrollbar-thumb": {
                  width: 3,
                  background: "#D9D9D9",
                  borderRadius: "1px",
                },
              }}
            >
              {tenants.map((tenant: ITenant) => (
                <Stack
                  key={tenant._id}
                  direction="row"
                  sx={{
                    padding: "5px 10px",
                    cursor: "pointer",
                    gap: 2,
                    alignItems: "center",
                  }}
                  onClick={() => {
                    if (tenant !== selectedTenant) {
                      dispatch(setSelectedClient(undefined));
                    }
                    if((pathname.includes("clients") || pathname.includes("partners")) && tenant.tenantType === "TRADEDIVE_CLIENT"){
                      navigate(`/dashboard/analytics`);
                    }
                    dispatch(setSelectedTenant(tenant));
                    closeClientPickerMenu();
                  }}
                >
                  <Avatar
                    src={tenant.companyLogo || company_placeholder}
                    sx={{
                      width: 34,
                      height: 34,
                    }}
                  />
                  <Box>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Typography
                        color="rgb(55, 65, 81)"
                        fontWeight={500}
                        fontSize={14}
                      >
                        {tenant.companyName}
                      </Typography>
                      <Chip
                        sx={{
                          backgroundColor:
                            tenant.tenantType === "SERVICE_PROVIDER"
                              ? "rgba(234, 67, 52, 1)"
                              : "rgba(52, 168, 83, 1)",
                          color: "#fff",
                          fontSize: 8,
                          fontWeight: 500,
                          height: 18,
                          borderRadius: 1,
                        }}
                        label={
                          tenant.tenantType === "SERVICE_PROVIDER" ? "SP" : "Client"
                        }
                        size="small"
                      />
                    </Stack>
                    <Typography color="#8E9494" fontSize={12}>
                      {tenant.companyEmail}
                    </Typography>
                  </Box>

                  {tenant._id === selectedTenant?._id ? (
                    <IconButton
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        backgroundColor: "#0B494B",
                        marginLeft: "auto",
                      }}
                    >
                      <Check sx={{ width: 12, height: 12, color: "#fff" }} />
                    </IconButton>
                  ) : null}
                </Stack>
              ))}
            </Box>
          )}
        </Box>
      </DialogMenu>
    </>
  );
};

export default TenantPicker;
